// src/components/navigation/NavSection.jsx
// Collapsible navigation section component

import React from 'react';
import { NavLink } from 'react-router-dom';
import { SECTION_LABELS } from '../../config/navConfig';
import { getBadgeForItem } from '../../util/badgeUtils';
import NavItem from './NavItem';

/**
 * NavSection - renders a collapsible section with header and items
 *
 * Props:
 * - section: Section key (work, completed, reference, system)
 * - items: Array of menu items for this section
 * - isExpanded: Whether section is currently expanded
 * - onToggle: Callback when section header is clicked
 * - counts: Badge counts object
 * - warnings: Warnings object (keyed by warningKey)
 * - userType: 'installer' or 'dealer'
 * - onNavigate: Callback when navigation occurs
 */
function NavSection({
  section,
  items,
  isExpanded,
  onToggle,
  counts,
  warnings,
  userType,
  onNavigate,
  submenuState,
  onSubmenuToggle
}) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`nav-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Section Header */}
      <div
        className="nav-section-header"
        onClick={() => onToggle(section)}
      >
        <span>{SECTION_LABELS[section]}</span>
        <span className={`chevron ${isExpanded ? 'open' : ''}`}>▼</span>
      </div>

      {/* Section Items */}
      {isExpanded && (
        <ul className="nav-section-items">
          {items.map(item => {
            // Handle items with submenus (like Automations)
            if (item.hasSubmenu && item.submenu) {
              const isSubmenuOpen = submenuState && submenuState[item.id];
              return (
                <React.Fragment key={item.id}>
                  <li>
                    <div
                      className={`text-light submenu-parent ${
                        window.location.pathname === item.route ? 'active' : ''
                      }`}
                      onClick={() => onSubmenuToggle && onSubmenuToggle(item.id)}
                      style={{ cursor: 'pointer', padding: '10px 15px' }}
                    >
                      {item.label}
                      <span className={`chevron ${isSubmenuOpen ? 'open' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </li>
                  {isSubmenuOpen && item.submenu.map(subItem => (
                    <li key={subItem.id} className="submenu-item">
                      <NavLink
                        activeClassName="active"
                        className="text-light"
                        to={subItem.route}
                        onClick={onNavigate}
                      >
                        {subItem.label}
                      </NavLink>
                    </li>
                  ))}
                </React.Fragment>
              );
            }

            // Regular menu item
            const badge = getBadgeForItem(item, counts);
            const warning = item.warningKey && warnings[item.warningKey];

            return (
              <NavItem
                key={item.id}
                item={item}
                badge={badge}
                warning={warning}
                userType={userType}
                onNavigate={onNavigate}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default NavSection;
